import React, { ChangeEvent, useCallback } from 'react';
import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Grid } from '@mui/material';
import { discounts, DiscountTypes } from 'api';
import { FormCard, FormInput } from 'components';
import DISCOUNTS from 'constants/discounts';
import { AutocompleteSearchItem } from 'api/types/common';
import { dataInitialSelectsOptions } from '../../../fields';
import CollectionsInput from '../CollectionsInput';

interface Props {
  control: Control<DiscountTypes.ItemCreateParams> | undefined;
  watch: UseFormWatch<DiscountTypes.ItemCreateParams>;
  setValue: UseFormSetValue<DiscountTypes.ItemCreateParams>;
  initialSelectsOptions:
    | ReturnType<typeof dataInitialSelectsOptions>
    | undefined;
}

const ProductsBlock = (props: Props): JSX.Element | null => {
  const { control, watch, setValue, initialSelectsOptions } = props;

  const brand = watch('brand');
  const countryTeam = watch('country_team');
  const shopId = watch('shop_id');
  const appliesTo = watch('applies_to');
  const discountAmountType = watch('discount_amount_type');

  const handleAppliesToChange = useCallback(
    (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      onChange: (value: string | string[] | undefined) => void,
    ) => {
      const value = event.target.value as DiscountTypes.AppliesToEnum;
      if (value === DiscountTypes.AppliesToEnum.SpecificCollections) {
        setValue('products', []);
      } else {
        setValue('collections', []);
      }

      onChange(value);
    },
    [setValue],
  );

  const handleAutocompleteChange = useCallback(
    (
      value: AutocompleteSearchItem | AutocompleteSearchItem[] | null,
      onChange: (value: string | string[] | null) => void,
    ) => {
      if (value && Array.isArray(value)) {
        const newValue = value.map((v) => {
          if ('originData' in v) {
            return v.originData;
          }
          return {
            id: v._id.$oid,
            title: v.name,
            image: v.image,
          };
        });

        onChange(newValue);
      }
    },
    [],
  );

  const getProducts = useCallback(
    (query: string) =>
      discounts
        .autocompleteProducts({
          product_title: query,
          brand,
          shop_id: shopId,
          country_team: countryTeam,
        })
        .then((res) =>
          res.data.products.edges.map((e) => ({
            name: e.node.title,
            _id: { $oid: e.node.id },
            image: e.node.images?.edges[0]?.node.url,
            originData: {
              id: e.node.id,
              title: e.node.title,
              image: e.node.images?.edges[0]?.node.url,
            },
          })),
        ),
    [brand, countryTeam, shopId],
  );

  return (
    <FormCard title="Products details">
      <Grid container spacing={2}>
        {discountAmountType ===
          DiscountTypes.DiscountAmountTypeEnum.ProductDiscount && (
          <Grid item xs={12}>
            <FormInput
              control={control}
              name="applies_to"
              inputType="radio"
              label="Applies to"
              options={DISCOUNTS.APPLIES_TO_OPTIONS}
              onChange={handleAppliesToChange}
              tooltip="After changing this field selected products and collections will be reset"
              rules={{ required: true }}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          {appliesTo === DiscountTypes.AppliesToEnum.SpecificCollections && (
            <CollectionsInput
              name="collections"
              initialValue={initialSelectsOptions?.collections}
              watch={watch}
              control={control}
            />
          )}
          {appliesTo === DiscountTypes.AppliesToEnum.SpecificProducts && (
            <FormInput
              control={control}
              name="products"
              inputType="autocompleteSearch"
              label="Products"
              multiple
              disabled={!shopId}
              initialValue={initialSelectsOptions?.products}
              onChange={handleAutocompleteChange}
              queryKey={discounts.endpoints.autocompleteProducts}
              queryFn={getProducts}
              rules={{
                required:
                  appliesTo === DiscountTypes.AppliesToEnum.SpecificProducts,
              }}
            />
          )}
        </Grid>
      </Grid>
    </FormCard>
  );
};

export default ProductsBlock;
